import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { selectUserCartSummary } from "../../features/store/cartSlice";
import { formatCurrency } from "../../utils/utils";
import { useAppSelector } from "../../hooks/hooks";
import CartPaymentModal from "../../components/PaymentMethods/CartPaymentModal";
import useClickOutside from "../../hooks/useClickOutside";

interface CheckoutProps {
  userId: string;
}

const Checkout: React.FC<CheckoutProps> = ({ userId }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const { user } = useAppSelector((state) => state.auth);
  const { data: appData } = useAppSelector((state) => state.appData);
  const { selectedCurrency } = useAppSelector((state) => state.locale);
  const currencies = appData?.currencies || [];

  const togglePaymentModal = () => {
    setShowPaymentModal((prev) => !prev);
  };

  const dgCoinCurrency =
    currencies.find((currency: any) => currency.code.toLowerCase() === "dgc") ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg coin"),
    );

  const dgPointCurrency =
    currencies.find((currency: any) => currency.code.toLowerCase() === "dgp") ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg point"),
    );
  // Correct positional arguments: (state, userId, targetCurrency, currencies, dgCoinCurrency)
  const summary = useSelector((state: RootState) =>
    selectUserCartSummary(
      state,
      userId,
      selectedCurrency,
      currencies,
      dgCoinCurrency,
      dgPointCurrency,
    ),
  );

  const modalContainerRef = useClickOutside<HTMLDivElement>(
    () => setShowPaymentModal(false),
    showPaymentModal,
  );

  const currencySymbol =
    selectedCurrency?.symbol || currencies[0]?.symbol || "";

  return (
    <div className="w-full flex flex-col dark:bg-[#FFFFFF1A] gap-2 border border-border-border1 dark:border-tabs p-4 rounded-lg">
      {summary.totalBonusMonetaryValue > 0 && (
        <div className="bg-[#FFF9E7] h-10 dark:bg-[#DC6803] p-3 rounded-3xl mb-6 flex items-center justify-center gap-1.5">
          <img src="/png/dg.png" alt="Coins" className="w-[24px] h-[24px]" />
          <p className="text-[10px] bp:text-sm text-[#DC6803] dark:text-white font-medium">
            Earn {currencySymbol}
            {formatCurrency(summary.totalBonusMonetaryValue)} in DG points bonus
            from this purchase
          </p>
        </div>
      )}

      <div>
        <div className="flex justify-between mb-1 bp:mb-2 font-inter">
          <span className="text-[#667085] dark:text-white text-xs bp:text-sm">
            Subtotal
          </span>
          <span className="text-black text-xs bp:text-sm dark:text-white">
            {currencySymbol} {formatCurrency(summary.subtotal)}
          </span>
        </div>

        <div className="flex justify-between mb-1 bp:mb-2 font-inter">
          <span className="text-[#667085] text-xs bp:text-sm dark:text-white">
            Discount
          </span>
          <span className="text-black dark:text-white text-xs bp:text-sm">
            {summary.effectiveDiscountPercent}%
          </span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-200 dark:border-white py-4 font-inter">
        <div className="flex justify-between mb-1 bp:mb-2 font-inter">
          <span className="text-[#667085] text-xs bp:text-lg dark:text-white font-medium">
            Total
          </span>
          <div className="flex items-center gap-2">
            <div className="bg-[#039EF2] text-white px-2 py-0.5 rounded-2xl flex items-center gap-1">
              <img
                src="/png/dg.png"
                alt="Coins"
                className="w-[14px] h-[14px]"
              />
              <p className="text-xs bp:text-sm font-semibold">
                {formatCurrency(summary.totalCoins)}
              </p>
            </div>

            <div className="bg-[#039EF2] text-white px-2 py-0.5 rounded-2xl flex items-center gap-1">
              <img
                src="/images/diamond.png"
                alt="Points"
                className="w-[14px] h-[14px]"
              />
              <p className="text-xs bp:text-sm font-semibold">
                {formatCurrency(summary.totalPoints)} {"pts"}
              </p>
            </div>
            <span className="text-xs bp:text-base text-black dark:text-white font-bold">
              {currencySymbol} {formatCurrency(summary.grandTotal)}
            </span>
          </div>
        </div>
      </div>

      <button
        className={`px-4 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2] ${
          summary.totalQuantity === 0 ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={() => setShowPaymentModal(true)}
        disabled={summary.totalQuantity === 0}
      >
        Checkout
      </button>

      <CartPaymentModal
        isOpen={showPaymentModal}
        userId={userId}
        targetCurrency={selectedCurrency}
        currencies={currencies}
        dgCoinCurrency={dgCoinCurrency}
        dgPointCurrency={dgPointCurrency}
        user={user}
        onClose={togglePaymentModal}
        clickref={modalContainerRef}
      />
    </div>
  );
};

export default Checkout;
